import { useState, useEffect, useMemo } from "react";
import cx from "classnames";
import Modal from 'react-modal';
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import ModalLayout, { smallModal } from "./modal-layout";
import styles from "./category-editor.module.css";
import { getChildren } from "./utils";
import IndeterminateCheckbox from "./indeterminate-checkbox";

const CategoryEditorModal = ({ existingCategory, isOpen, onSave, onCancel }) => {
    const [editingCategory, setEditingCategory] = useState(null);

    useEffect(() => {
        if(isOpen) {
            if (existingCategory) {
                setEditingCategory(existingCategory);
            } else {
                setEditingCategory({
                    name: "",
                    color: "#6B89FF"
                });
            }
        } else {
            setEditingCategory(null);
        }
    }, [isOpen]);

    const renderContent = () => {
        if(editingCategory === null) {
            return null;
        }
        return (
            <>
                <input
                    value={editingCategory.name}
                    onChange={e => setEditingCategory({
                        ...editingCategory,
                        name: e.target.value
                    })}
                    autoFocus={true}
                />
                <input
                    type="color"
                    value={editingCategory.color}
                    onChange={e => setEditingCategory({
                        ...editingCategory,
                        color: e.target.value
                    })}
                />
            </>
        );
    };

    return (
        <Modal
            isOpen={editingCategory !== null}
            onRequestClose={onCancel}
            style={smallModal}
        >
            <ModalLayout
                title={!!existingCategory ? "Edit category" : "Create new category"}
                onCancel={onCancel}
                onSubmit={() => onSave(editingCategory)}
                className={styles.categoryModalEditor}
            >
                { renderContent() }
            </ModalLayout>
        </Modal>
    );
}

const Category = ({
    categoryUri,
    globalContext,
    categoriesByUri,
    checkedCategories,
    categoriesMeta,
    indent,
    onChange,
    onMetaChange,
    onCreate,
    onMove,
    onCheckedChanged
}) => {
    const [editingCategory, setEditingCategory] = useState(false);
    const [creatingCategory, setCreatingCategory] = useState(false);

    const category = categoriesByUri[categoryUri];
    const children = getChildren(categoriesByUri, categoryUri);
    const expanded = globalContext.expandAll || !!categoriesMeta[categoryUri]?.expanded;
    const expandableAndExpanded = expanded && children.length !== 0;

    const [{ isDragging }, dragRef, dragPreview] = useDrag({
        type: "CATEGORY",
        item: { categoryUri },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const isAncestor = other => {
        var test = category;
        while (test != null) {
            if (test.uri === other) {
                return true;
            }
            test = categoriesByUri[test.parent];
        }
        return false;
    };

    const [, dropUnderneathRef] = useDrop({
        accept: "CATEGORY",
        hover: draggedItem => {
            if (!isAncestor(draggedItem.categoryUri)) {
                onMove(draggedItem.categoryUri, categoryUri, false);
            }
        }
    });

    const [, dropInsideRef] = useDrop({
        accept: "CATEGORY",
        hover: draggedItem => {
            if (!isAncestor(draggedItem.categoryUri)) {
                onMove(draggedItem.categoryUri, categoryUri, true);
                onMetaChange(categoryUri, { expanded: true });
            }
        }
    });

    const closeCategoryEditorModal = () => {
        setEditingCategory(false);
        setCreatingCategory(false);
    };

    return (
        <>
            <div
                ref={dragPreview}
                className={styles.category}
            >
                <div className={styles.firstCategoryPiece} ref={dropUnderneathRef}>
                    <span style={{ width: `${indent * 16}px` }}></span>
                    {onMove &&
                        <span
                            ref={dragRef}
                            className={cx("material-icons", styles.dragHandle)}
                        >drag_handle</span>
                    }
                    <button
                        onClick={e => onMetaChange(categoryUri, {
                            expanded: !expanded
                        })}
                        className={cx("material-icons", { [styles.nonexpandable]: children.length === 0 })}
                    >{expandableAndExpanded ? "keyboard_arrow_down" : "keyboard_arrow_right"}</button>
                    {!!checkedCategories &&
                        <IndeterminateCheckbox
                            checked={checkedCategories[category.uri]}
                            onChange={e => onCheckedChanged(categoryUri, e.target.checked)}
                        />
                    }
                </div>
                <div className={styles.secondCategoryPiece} ref={dropInsideRef}>
                    <label
                        onDoubleClick={() => onChange && setEditingCategory(true)}
                        style={{
                            borderColor: category.color,
                            backgroundColor: `${category.color}11`
                        }}
                        className={styles.categoryName}
                    >{category.name}</label>
                    {onChange &&
                        <button
                            onClick={() => setEditingCategory(true)}
                            className={cx("material-icons")}
                        >edit</button>
                    }
                    {onCreate &&
                        <button
                            onClick={() => setCreatingCategory(true)}
                            className={cx("material-icons")}
                        >add</button>
                    }
                    {onChange &&
                        <button
                            onClick={() => {
                                if (window.confirm(`Are you sure you want to delete ${category.name} ?`)) {
                                    onChange(categoryUri, { deleted: true });
                                }
                            }}
                            className={cx("material-icons")}
                        >delete</button>
                    }
                </div>
            </div>
            {expandableAndExpanded &&
                <CategoriesList
                    categoryUris={children.map(c => c.uri)}
                    checkedCategories={checkedCategories}
                    globalContext={globalContext}
                    categoriesByUri={categoriesByUri}
                    categoriesMeta={categoriesMeta}
                    indent={indent + 1}
                    onChange={onChange}
                    onMetaChange={onMetaChange}
                    onCreate={onCreate}
                    onMove={onMove}
                    onCheckedChanged={onCheckedChanged}
                />
            }
            <CategoryEditorModal
                existingCategory={editingCategory ? category : null}
                isOpen={!!editingCategory || !!creatingCategory}
                onSave={newCategory => {
                    if (editingCategory) {
                        onChange(categoryUri, newCategory);
                    }
                    if (creatingCategory) {
                        onCreate(categoryUri, newCategory);
                        onMetaChange(categoryUri, { expanded: true });
                    }
                    closeCategoryEditorModal();
                }}
                onCancel={() => closeCategoryEditorModal()}
            />
        </>
    );
};

const CategoriesList = ({
    categoryUris,
    globalContext,
    categoriesByUri,
    checkedCategories,
    categoriesMeta,
    indent,
    onChange,
    onMetaChange,
    onCreate,
    onMove,
    onCheckedChanged
}) => {
    const [creatingNewCategory, setCreatingNewCategory] = useState(false);
    return (
        <div>
            { !indent && onCreate &&
                <>
                    <button
                        onClick={() => setCreatingNewCategory(true)}
                        className={styles.createTopLevelCategoryButton}
                    >Create new category</button>
                    <hr/>
                </>
            }
            { categoryUris.map(categoryUri =>
                <Category
                    key={categoryUri}
                    categoryUri={categoryUri}
                    globalContext={globalContext || {}}
                    categoriesByUri={categoriesByUri}
                    checkedCategories={checkedCategories}
                    categoriesMeta={categoriesMeta}
                    indent={indent || 0}
                    onChange={onChange}
                    onMetaChange={onMetaChange}
                    onCreate={onCreate}
                    onMove={onMove}
                    onCheckedChanged={onCheckedChanged}
                />
            ) }
            <CategoryEditorModal
                isOpen={creatingNewCategory}
                onSave={newCategory => {
                    onCreate(null, newCategory);
                    setCreatingNewCategory(false);
                }}
                onCancel={() => setCreatingNewCategory(false)}
            />
        </div>
    );
}

export default ({
    categories,
    uncategorizedCategory,
    showSearchFilter,
    checkedCategories, // An object of categoryUri to boolean (true, null or false)
    onCategoriesChanged,
    onCheckedCategoriesChanged, // Receives a new copy of the checkCategories object
    className
}) => {
    const [searchFilter, setSearchFilter] = useState("");
    const [categoriesMeta, setCategoriesMeta] = useState({});

    const categoriesByUri = useMemo(() => Object.fromEntries(categories.map(c => [c.uri, c])), [categories]);

    const filteredCategoriesByUri = useMemo(() => {
        if (searchFilter === "") {
            return categoriesByUri;
        }
        const remaining = {};
        const addCategory = category => {
            remaining[category.uri] = category;
            const parentCategory = categoriesByUri[category.parent];
            if (parentCategory) {
                addCategory(parentCategory);
            }
        };
        for (const category of Object.values(categoriesByUri)) {
            if (category.name.toLowerCase().includes(searchFilter.toLowerCase())) {
                addCategory(category);
            }
        }
        return remaining;
    }, [categoriesByUri, searchFilter]);

    const topLevelCategories = getChildren(filteredCategoriesByUri, null).map(c => c.uri);

    const checkedCategoriesWithAncestors = useMemo(() => {
        if(!checkedCategories) {
            return null;
        }
        const result = {};
        const addCategory = category => {
            result[category.uri] = null;
            const parentCategory = categoriesByUri[category.parent];
            if (parentCategory) {
                addCategory(parentCategory);
            }
        };
        Object
            .entries(checkedCategories)
            .filter(([ _, checked ]) => checked !== false)
            .map(([ c, _ ]) => categoriesByUri[c])
            .filter(c => !!c)
            .forEach(c => addCategory(c));
        return {
            ...result,
            ...checkedCategories
        };
    }, [ categoriesByUri, checkedCategories ]);

    const convertArrayToMapping = categories =>
        Object.fromEntries(
            categories.map((c, i) => ([c.uri, { ...c, order: i }]))
        );

    return (
        <div className={cx(styles.categoryEditor, className)}>
            {showSearchFilter &&
                <input
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                    onKeyDown={event => {
                        if (event.key === "Enter") {
                            const bottomCategoryUris = Object
                                .keys(filteredCategoriesByUri)
                                .filter(categoryUri => getChildren(filteredCategoriesByUri, categoryUri).length === 0);
                            if(bottomCategoryUris.length === 1) {
                                onCheckedCategoriesChanged({
                                    ...checkedCategories,
                                    [ bottomCategoryUris[0] ]: true
                                });
                            }
                        }
                    }}
                    placeholder="Search..."
                    type="text"
                    className={styles.searchInput}
                />
            }
            <DndProvider backend={HTML5Backend}>
                <CategoriesList
                    categoryUris={topLevelCategories}
                    checkedCategories={checkedCategoriesWithAncestors}
                    globalContext={{ expandAll: searchFilter !== "" }}
                    categoriesByUri={filteredCategoriesByUri}
                    categoriesMeta={categoriesMeta}
                    onChange={onCategoriesChanged && ((categoryUri, delta) => {
                        onCategoriesChanged(
                            categories.map(c => c.uri === categoryUri ? {
                                ...c, ...delta
                            } : c)
                        );
                    })}
                    onMetaChange={(categoryUri, delta) => {
                        setCategoriesMeta(categoriesMeta => ({
                            ...categoriesMeta,
                            [categoryUri]: {
                                ...categoriesMeta[categoryUri],
                                ...delta
                            }
                        }));
                    }}
                    onCreate={onCategoriesChanged && ((parentUri, delta) => {
                        const orderedSiblings = getChildren(categoriesByUri, parentUri);
                        const newCategory = {
                            ...delta,
                            parent: parentUri,
                            uri: `new:${Date.now().toString()}`
                        };
                        orderedSiblings.unshift(newCategory);
                        return onCategoriesChanged(
                            Object.values({
                                ...categoriesByUri,
                                ...convertArrayToMapping(orderedSiblings)
                            })
                        );
                    })}
                    onMove={onCategoriesChanged && ((toMoveUri, moveToUri, asChild) => {
                        const toMove = categoriesByUri[toMoveUri];
                        const moveTo = categoriesByUri[moveToUri];
                        const toMoveGroup = getChildren(
                            categoriesByUri, toMove.parent
                        );
                        var moveToGroup;
                        if (asChild) {
                            moveToGroup = getChildren(
                                categoriesByUri, moveTo.uri
                            );
                        } else {
                            if (toMove.parent === moveTo.parent) {
                                moveToGroup = toMoveGroup;
                            } else {
                                moveToGroup = getChildren(
                                    categoriesByUri, moveTo.parent
                                );
                            }
                        }
                        const toMoveIndex = toMoveGroup.indexOf(toMove);
                        const moveToIndex = asChild ? 0 : moveToGroup.indexOf(moveTo);
                        toMoveGroup.splice(toMoveIndex, 1);
                        moveToGroup.splice(moveToIndex, 0, {
                            ...toMove,
                            parent: asChild ? moveTo.uri : moveTo.parent
                        });
                        onCategoriesChanged(
                            Object.values({
                                ...categoriesByUri,
                                ...convertArrayToMapping(moveToGroup),
                                ...convertArrayToMapping(toMoveGroup)
                            })
                        );
                    })}
                    onCheckedChanged={(categoryUri, checked) => {
                        const result = { ...checkedCategories };
                        var parentSearchUri = categoryUri;
                        const unsetCategory = uri => {
                            if(result[uri] === true) {
                                result[uri] = false;
                            } else if(result === null) {
                                delete result[uri]
                            }
                        };
                        // Unsets all categories "above" us
                        while (parentSearchUri !== null) {
                            unsetCategory(parentSearchUri);
                            parentSearchUri = categoriesByUri[parentSearchUri].parent;
                        }
                        // Unsets all categories "below" us
                        const searchChildren = parentUri => {
                            for (const child of getChildren(categoriesByUri, parentUri)) {
                                searchChildren(child.uri);
                            }
                            unsetCategory(parentUri);
                        };
                        searchChildren(categoryUri);
                        result[categoryUri] = checked;
                        onCheckedCategoriesChanged(result);
                    }}
                />
            </DndProvider>
        </div>
    );
};
